import type {
  AdminCommunicationCampaign,
  AdminCommunicationCampaignListResponse,
  AdminCommunicationCampaignRecipientListResponse,
  AdminCommunicationCampaignResponse,
  CommunicationAudienceFilter,
  CommunicationAudiencePreview,
  CommunicationAudiencePreviewResponse,
  CommunicationCampaignKind,
  CommunicationCampaignStatus,
  SendCommunicationCampaignRequest,
} from '@mds/shared/types/communication';
import type { NotificationDeliveryStatus } from '@mds/shared/types/notification-history';

import { api } from '@/lib/api';

/**
 * Announcements and invitations to CRM customers — see
 * `backend/docs/communications.md`. Only customers who agreed to email are
 * ever counted, and a send is refused unless it names the count the latest
 * preview returned.
 */

export const previewAudience = (
  audience: CommunicationAudienceFilter,
): Promise<CommunicationAudiencePreview> =>
  api
    .post<CommunicationAudiencePreviewResponse>('admin/communications/audience-preview', {
      audience,
    })
    .then((r) => r.data);

/**
 * Irreversible. `requestId` is generated when the administrator confirms and
 * reused only to retry that same request, so a lost response cannot send twice.
 */
export const sendCampaign = (
  body: SendCommunicationCampaignRequest,
): Promise<AdminCommunicationCampaign> =>
  api
    .post<AdminCommunicationCampaignResponse>('admin/communications/campaigns', body)
    .then((r) => r.data);

/** A type alias, so it carries the index signature the client's `query` needs. */
export type CampaignQuery = {
  page?: number;
  limit?: number;
  q?: string;
  kind?: CommunicationCampaignKind;
  status?: CommunicationCampaignStatus;
  sort?: 'createdAt' | 'subject' | 'recipientCount' | 'status';
  direction?: 'asc' | 'desc';
};

export const listCampaigns = (query: CampaignQuery = {}) =>
  api.get<AdminCommunicationCampaignListResponse>('admin/communications/campaigns', { query });

export const getCampaign = (campaignId: string): Promise<AdminCommunicationCampaign> =>
  api
    .get<AdminCommunicationCampaignResponse>(`admin/communications/campaigns/${campaignId}`)
    .then((r) => r.data);

export type RecipientQuery = {
  page?: number;
  limit?: number;
  q?: string;
  status?: NotificationDeliveryStatus;
  sort?: 'createdAt' | 'recipientName' | 'recipientEmail' | 'deliveryStatus';
  direction?: 'asc' | 'desc';
};

export const listRecipients = (campaignId: string, query: RecipientQuery = {}) =>
  api.get<AdminCommunicationCampaignRecipientListResponse>(
    `admin/communications/campaigns/${campaignId}/recipients`,
    { query },
  );
